/**
 * [TD-47.6a-ui Gap 47.6a-C + v1.1 Redline 3] ChatPanelWithFilter - merged
 * chat + Adam stream with filter chip dropdown + mute-toggle.
 *
 * v1.1 merges Adam's `activityVoice` rows INTO the same panel as managers'
 * `chatMessages`. Both stream into one timestamp-interleaved feed with
 * visual differentiation (Adam rows render with an orange "ADAM" pill;
 * manager rows keep their existing chat bubble treatment).
 *
 * Filter chips (v1.1 enum):
 *   "manager" → "Chat" / "adam" → "Adam" / "system" → "Round Starts"
 *
 * Audio control (v1.1 Q2 verdict A revised):
 *   Mute toggle ONLY in the header - OS handles volume. Live mute state
 *   passes through `audioControls.muted` + `audioControls.toggleMute`.
 *
 * Persistence (banked under TD-64):
 *   Chip state lives in component-local state for v1. Future enhancement:
 *   localStorage `pgf_chat_filter_chips:{leagueId}`.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Volume2, VolumeX, Send, AlertCircle } from "lucide-react";
import { motion } from "framer-motion";
import ChatFilterDropdown from "./ChatFilterDropdown";
import { useDraft } from "../../contexts/DraftContext";
import { useAuth } from "../../contexts/AuthContext";

const DEFAULT_ALL_ON = new Set(["manager", "adam", "system"]);
const CHAT_MAX_CHARS = 500;
const COUNTER_AMBER_AT = 450;

// Persona ring colors preserved from ChatPanel (Phase 2c.6-C.1).
const PERSONA_STYLES = {
  SmartMoney: { bubble: "bg-[#1B2A38] border border-[#5BB7E5]/55", name: "text-[#7CC8E8]", label: "Smart Money" },
  TheGrinder: { bubble: "bg-[#222428] border border-[#9BA1A8]/55", name: "text-[#C9CDD3]", label: "The Grinder" },
  ValueHunter: { bubble: "bg-[#2A1F12] border border-[#E8A24B]/60", name: "text-[#F2BF7A]", label: "Value Hunter" },
};

/**
 * Project an `activityVoice` row into a normalized chat-stream row.
 * Adam rows are tagged with `_kind: "adam"` so the renderer can branch.
 */
function _projectAdamRow(id, v) {
  return {
    _kind: "adam",
    id: `adam-${id}`,
    text: v.voiceText || "",
    activityType: v.activityType || null,
    createdAt: v.createdAt || null,
    // Tag with the v1.1 enum for filter parity.
    chat_filter_category: "adam",
  };
}

function _projectChatRow(msg, i) {
  return {
    _kind: "chat",
    id: msg.id || `chat-${msg.timestamp || ""}-${i}`,
    sender_id: msg.sender_id,
    sender_name: msg.sender_name,
    text: msg.text,
    is_cpu: msg.is_cpu,
    is_system: msg.is_system,
    persona: msg.persona,
    timestamp: msg.timestamp,
    chat_filter_category: msg.chat_filter_category ?? "manager",
  };
}

function _rowTime(row) {
  const t =
    row._kind === "adam"
      ? new Date(row.createdAt || 0).getTime()
      : new Date(row.timestamp || 0).getTime();
  return Number.isFinite(t) ? t : 0;
}

export default function ChatPanelWithFilter({ audioControls = null, dataHook = useDraft }) {
  const {
    chatMessages,
    sendChat,
    chatRejected,
    clearChatRejected,
    activityVoice,
    loadOlderChat,
  } = dataHook();
  const { user } = useAuth();
  const myId = user?.id;

  const [filterChips, setFilterChips] = useState(DEFAULT_ALL_ON);
  const [input, setInput] = useState("");

  // Merge chatMessages + activityVoice into a single timestamp-sorted stream.
  // Adam rows: only voice_source === "llm" || "auctioneer" (matches the
  // standalone LiveActivityFeed filter - D.6 silent-fallback guardrail).
  const merged = useMemo(() => {
    const chatRows = (chatMessages || []).map(_projectChatRow);
    const adamRows = Object.entries(activityVoice || {})
      .filter(([, v]) => v.voiceSource === "llm" || v.voiceSource === "auctioneer")
      .map(([id, v]) => _projectAdamRow(id, v));
    const all = [...chatRows, ...adamRows];
    all.sort((a, b) => _rowTime(a) - _rowTime(b));
    return all;
  }, [chatMessages, activityVoice]);

  const filtered = useMemo(() => {
    if (filterChips.size === DEFAULT_ALL_ON.size) return merged;
    return merged.filter((r) => {
      const cat = r.chat_filter_category;
      if (cat === null || cat === undefined) return true;
      return filterChips.has(cat);
    });
  }, [merged, filterChips]);

  // [HF chat-autoscroll] Mirror of ChatPanel's user-scroll grace period.
  //   • At-bottom: every new row auto-scrolls.
  //   • Scrolled-up: 10s grace; then quick snap back to latest row.
  const scrollContainerRef = useRef(null);
  const bottomRef = useRef(null);
  const userScrolledAwayRef = useRef(false);
  const graceTimerRef = useRef(null);
  const SCROLL_AT_BOTTOM_THRESHOLD_PX = 24;
  const GRACE_PERIOD_MS = 10000;

  useEffect(() => {
    const node = scrollContainerRef.current;
    if (!node) return undefined;
    const onScroll = () => {
      const distanceFromBottom = node.scrollHeight - node.scrollTop - node.clientHeight;
      const atBottom = distanceFromBottom <= SCROLL_AT_BOTTOM_THRESHOLD_PX;
      // [Founder reconnect-parity - Feb 2026] Scroll-to-top → load
      // older chat page via cursor. Preserve scroll position by
      // snapshotting scrollHeight before the fetch and restoring
      // offset after the DOM grows.
      if (node.scrollTop <= 80 && typeof loadOlderChat === "function") {
        const prevHeight = node.scrollHeight;
        Promise.resolve(loadOlderChat()).then(() => {
          // requestAnimationFrame so the new rows have laid out.
          requestAnimationFrame(() => {
            const delta = node.scrollHeight - prevHeight;
            if (delta > 0) node.scrollTop = node.scrollTop + delta;
          });
        }).catch(() => { /* non-fatal */ });
      }
      if (atBottom) {
        userScrolledAwayRef.current = false;
        if (graceTimerRef.current) {
          clearTimeout(graceTimerRef.current);
          graceTimerRef.current = null;
        }
        return;
      }
      userScrolledAwayRef.current = true;
      if (graceTimerRef.current) clearTimeout(graceTimerRef.current);
      graceTimerRef.current = setTimeout(() => {
        userScrolledAwayRef.current = false;
        graceTimerRef.current = null;
        bottomRef.current?.scrollIntoView({ behavior: "auto", block: "end" });
      }, GRACE_PERIOD_MS);
    };
    node.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      node.removeEventListener("scroll", onScroll);
      if (graceTimerRef.current) {
        clearTimeout(graceTimerRef.current);
        graceTimerRef.current = null;
      }
    };
  }, [loadOlderChat]);

  useEffect(() => {
    if (userScrolledAwayRef.current) return;
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [filtered.length]);

  const handleSend = () => {
    const text = input.trim();
    if (!text || text.length > CHAT_MAX_CHARS) return;
    sendChat?.(text);
    setInput("");
  };
  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const len = input.length;
  const overCap = len > CHAT_MAX_CHARS;
  const counterColor =
    overCap || len === CHAT_MAX_CHARS
      ? "text-[#FF4444]"
      : len >= COUNTER_AMBER_AT
      ? "text-[#FF9933]"
      : "text-[rgba(255,255,255,0.35)]";

  return (
    <div
      className="bg-[#1C1C1C] border border-[#252525] rounded-2xl flex flex-col h-full overflow-hidden"
      data-testid="chat-panel-with-filter"
    >
      {/* Header strip: title · mute · filter dropdown */}
      <div className="px-3 py-2 border-b border-[#252525] flex items-center justify-between shrink-0 gap-2">
        <span className="font-display font-bold text-xs uppercase tracking-wider text-[#FF6B00]">
          Draft Chat
        </span>
        <div className="flex items-center gap-2">
          {audioControls ? (
            <button
              onClick={audioControls.toggleMute}
              data-testid={audioControls.muted ? "chat-unmute-btn" : "chat-mute-btn"}
              title={audioControls.muted ? "Unmute Adam" : "Mute Adam"}
              className="transition-colors shrink-0 hover:text-[#FF6B00]"
              style={{ color: audioControls.muted ? "#888" : "#F5F5F5" }}
            >
              {audioControls.muted ? <VolumeX size={13} /> : <Volume2 size={13} />}
            </button>
          ) : null}
          <ChatFilterDropdown value={filterChips} onChange={setFilterChips} />
        </div>
      </div>

      {/* Messages */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto px-3 py-2 space-y-1.5 min-h-0"
        data-testid="chat-merged-scroll"
      >
        {filtered.length === 0 ? (
          <p className="font-body text-xs text-[rgba(255,255,255,0.25)] text-center pt-4">
            Draft chat is live. Talk your trash. Adam will call the action.
          </p>
        ) : (
          filtered.map((row) =>
            row._kind === "adam" ? (
              <AdamRow key={row.id} row={row} />
            ) : (
              <ChatRow key={row.id} row={row} myId={myId} />
            )
          )
        )}
        <div ref={bottomRef} />
      </div>

      {/* CHAT_REJECTED inline banner */}
      {chatRejected && (
        <div
          className="mx-3 mb-1 px-2 py-1 rounded-md bg-[#FF4444]/10 border border-[#FF4444]/40 flex items-center gap-1.5 shrink-0"
          data-testid="auction-chat-rejected"
        >
          <AlertCircle size={11} className="text-[#FF4444]" />
          <span className="font-body text-[10px] text-[#FF4444]">
            {chatRejected.reason === "TOO_LONG"
              ? `Message too long (${chatRejected.len}/${chatRejected.max_chars}). Trim it down and try again.`
              : "Message rejected."}
          </span>
          <button
            onClick={clearChatRejected}
            className="ml-auto text-[10px] text-[#FF4444] hover:underline"
          >
            dismiss
          </button>
        </div>
      )}

      {/* Input */}
      <div className="flex items-center gap-2 px-3 py-2 border-t border-[#252525] shrink-0">
        <div className="flex-1 flex flex-col">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Say something..."
            maxLength={CHAT_MAX_CHARS}
            className={`bg-[#252525] border ${
              overCap ? "border-[#FF4444]/60" : "border-transparent focus:border-[#FF6B00]/40"
            } text-[#F5F5F5] rounded-lg px-3 py-1.5 text-xs font-body outline-none transition-colors placeholder-[rgba(255,255,255,0.25)]`}
            data-testid="auction-chat-input"
          />
          {len > 0 && (
            <span
              className={`font-body text-[9px] mt-0.5 self-end ${counterColor}`}
              data-testid="auction-chat-counter"
            >
              {len}/{CHAT_MAX_CHARS}
            </span>
          )}
        </div>
        <button
          onClick={handleSend}
          disabled={!input.trim() || overCap}
          title={overCap ? "Message too long" : "Send"}
          className="w-7 h-7 bg-[#FF6B00] hover:bg-[#FF8533] disabled:opacity-40 rounded-lg flex items-center justify-center transition-all active:scale-95 shrink-0"
          data-testid="auction-chat-send"
        >
          <Send size={12} className="text-white" />
        </button>
      </div>
    </div>
  );
}

function AdamRow({ row }) {
  // [v1.1 Redline 3] Adam rows render with the orange ADAM pill prefix.
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex items-start gap-2"
      data-testid="chat-adam-row"
    >
      <span
        className="font-display font-black text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-md shrink-0 bg-[#FF6B00]/20 text-[#FF6B00] mt-0.5"
        data-testid="chat-adam-pill"
      >
        ADAM
      </span>
      <p className="font-body text-xs text-[#F5F5F5] leading-snug flex-1 min-w-0 break-words">
        {row.text}
      </p>
    </motion.div>
  );
}

function ChatRow({ row, myId }) {
  const isMe = row.sender_id && row.sender_id === myId;
  const personaStyle =
    row.is_cpu && row.persona ? PERSONA_STYLES[row.persona] : null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex gap-2 ${isMe ? "flex-row-reverse" : "flex-row"}`}
      data-testid="auction-chat-msg"
      data-persona={row.is_cpu && row.persona ? row.persona : undefined}
    >
      <div
        className={`max-w-[80%] rounded-lg px-2.5 py-1.5 ${
          row.is_system
            ? "bg-[#FF4444]/10 border border-[#FF4444]/30"
            : isMe
            ? "bg-[#FF6B00]/20 border border-[#FF6B00]/30"
            : personaStyle
            ? personaStyle.bubble
            : row.is_cpu
            ? "bg-[#252525] border border-[#2E2E2E]"
            : "bg-[#252525] border border-[#333]"
        }`}
      >
        {!isMe && (
          <p
            className={`font-display font-bold text-[10px] uppercase tracking-wide mb-0.5 ${
              row.is_system
                ? "text-[#FF4444]"
                : personaStyle
                ? personaStyle.name
                : row.is_cpu
                ? "text-[rgba(255,255,255,0.40)]"
                : "text-[#00C853]"
            }`}
          >
            {row.sender_name}
          </p>
        )}
        <p className="font-body text-xs text-[#F5F5F5] leading-snug whitespace-pre-wrap break-words">
          {row.text}
        </p>
      </div>
    </motion.div>
  );
}
